import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { rfqApi, categoriesApi } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import './SubmitRFQPage.css';

const SubmitRFQPage = () => {
  const { user } = useAuth();
  const { selectedCurrency, currencies } = useCurrency();
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitResult, setSubmitResult] = useState(null);

  const [form, setForm] = useState({
    category: '',
    subCategory: '',
    details: '',
    quantity: '',
    unit: 'Units',
    budget: '',
    budgetCurrency: selectedCurrency.code,
    attachment: '',
    companyName: user?.companyName || '',
    fullName: user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '',
    email: user?.email || '',
    phoneNumber: user?.phone || '',
    country: user?.country || '',
    countryCode: '',
    rfqType: 'product'
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categoriesApi.getAll();
        setCategories(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error loading categories:', err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (form.category) {
      const cat = categories.find(c => c.name === form.category);
      if (cat?.subcategories) {
        setSubCategories(cat.subcategories);
      } else {
        setSubCategories([]);
      }
    } else {
      setSubCategories([]);
    }
  }, [form.category, categories]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.category.trim()) newErrors.category = 'Category is required';
    if (!form.details.trim()) newErrors.details = 'Product requirement details are required';
    if (!form.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!form.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Invalid email format';
    if (!form.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
    if (!form.country.trim()) newErrors.country = 'Country is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const submitData = {
        ...form,
        quantity: form.quantity ? parseInt(form.quantity) : null,
        budget: form.budget ? parseFloat(form.budget) : null,
        userId: user?.id || null,
        isPublicOffer: true
      };

      const result = await rfqApi.create(submitData);
      setSubmitResult(result);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting RFQ:', err);
      setErrors({ submit: err.response?.data?.error || 'Failed to submit request. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="submit-rfq-page">
        <Header />
        <main className="srfq-main">
          <div className="srfq-success">
            <div className="srfq-success-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22 4 12 14.01 9 11.01" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>RFQ Submitted Successfully!</h2>
            <p className="srfq-success-info">
              Your request for quotation has been submitted and sent to {submitResult?.matchedSellers || 0} matching supplier(s).
              {submitResult?._info && <span className="srfq-info-detail">{submitResult._info}</span>}
            </p>
            <p className="srfq-success-id">RFQ Reference: <strong>#{submitResult?.id}</strong></p>
            <div className="srfq-success-actions">
              <button className="srfq-btn-primary" onClick={() => { setSubmitted(false); setForm(prev => ({ ...prev, category: '', subCategory: '', details: '', quantity: '', budget: '', attachment: '' })); }}>
                Submit Another RFQ
              </button>
              <Link to="/buy-leads" className="srfq-btn-secondary">View Buy Leads</Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="submit-rfq-page">
      <Header />
      <main className="srfq-main">
        <div className="srfq-breadcrumb">
          <Link to="/" className="srfq-bc-link">Home</Link>
          <span className="srfq-bc-sep">/</span>
          <span className="srfq-bc-active">Submit RFQ</span>
        </div>

        <div className="srfq-container">
          <div className="srfq-header">
            <h1>Request for Quotation</h1>
            <p>Tell suppliers what you need and get quotes from verified manufacturers and suppliers.</p>
          </div>

          <form className="srfq-form" onSubmit={handleSubmit}>
            {errors.submit && <div className="srfq-error-banner">{errors.submit}</div>}

            <div className="srfq-section">
              <h3 className="srfq-section-title">Product / Service Details</h3>
              
              <div className="srfq-row">
                <div className="srfq-field">
                  <label>Category <span className="required">*</span></label>
                  <select name="category" value={form.category} onChange={handleChange} className={errors.category ? 'error' : ''}>
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                  {errors.category && <span className="srfq-error">{errors.category}</span>}
                </div>
                <div className="srfq-field">
                  <label>Sub-Category</label>
                  <select name="subCategory" value={form.subCategory} onChange={handleChange}>
                    <option value="">Select Sub-Category</option>
                    {subCategories.map(sub => (
                      <option key={sub.id} value={sub.name}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="srfq-field">
                <label>Requirement Details <span className="required">*</span></label>
                <textarea
                  name="details"
                  value={form.details}
                  onChange={handleChange}
                  placeholder="Describe what you need in detail (product specifications, features, materials, etc.)"
                  rows={4}
                  className={errors.details ? 'error' : ''}
                />
                {errors.details && <span className="srfq-error">{errors.details}</span>}
              </div>

              <div className="srfq-row srfq-row-3">
                <div className="srfq-field">
                  <label>Quantity</label>
                  <input type="number" name="quantity" value={form.quantity} onChange={handleChange} placeholder="e.g. 100" min="1" />
                </div>
                <div className="srfq-field">
                  <label>Unit</label>
                  <select name="unit" value={form.unit} onChange={handleChange}>
                    <option value="Units">Units</option>
                    <option value="Pieces">Pieces</option>
                    <option value="Tons">Tons</option>
                    <option value="Kg">Kg</option>
                    <option value="Meters">Meters</option>
                    <option value="Sq Meters">Sq Meters</option>
                    <option value="Sets">Sets</option>
                    <option value="Boxes">Boxes</option>
                    <option value="Pallets">Pallets</option>
                    <option value="Containers">Containers</option>
                  </select>
                </div>
                <div className="srfq-field">
                  <label>Budget (Optional)</label>
                  <div className="srfq-budget-row">
                    <select name="budgetCurrency" value={form.budgetCurrency} onChange={handleChange} className="srfq-currency-select">
                      {currencies.map(c => (
                        <option key={c.code} value={c.code}>{c.code}</option>
                      ))}
                    </select>
                    <input type="number" name="budget" value={form.budget} onChange={handleChange} placeholder="Amount" min="0" step="0.01" />
                  </div>
                </div>
              </div>

              <div className="srfq-field">
                <label>Attachment URL (Optional)</label>
                <input type="url" name="attachment" value={form.attachment} onChange={handleChange} placeholder="Link to specification document, drawing, or image" />
              </div>
            </div>

            <div className="srfq-section">
              <h3 className="srfq-section-title">Contact Details</h3>

              <div className="srfq-row">
                <div className="srfq-field">
                  <label>Company Name <span className="required">*</span></label>
                  <input type="text" name="companyName" value={form.companyName} onChange={handleChange} placeholder="Your company name" className={errors.companyName ? 'error' : ''} />
                  {errors.companyName && <span className="srfq-error">{errors.companyName}</span>}
                </div>
                <div className="srfq-field">
                  <label>Full Name <span className="required">*</span></label>
                  <input type="text" name="fullName" value={form.fullName} onChange={handleChange} placeholder="Your full name" className={errors.fullName ? 'error' : ''} />
                  {errors.fullName && <span className="srfq-error">{errors.fullName}</span>}
                </div>
              </div>

              <div className="srfq-row">
                <div className="srfq-field">
                  <label>Email <span className="required">*</span></label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" className={errors.email ? 'error' : ''} />
                  {errors.email && <span className="srfq-error">{errors.email}</span>}
                </div>
                <div className="srfq-field">
                  <label>Phone Number <span className="required">*</span></label>
                  <input type="tel" name="phoneNumber" value={form.phoneNumber} onChange={handleChange} placeholder="+971-XXX-XXXXXXX" className={errors.phoneNumber ? 'error' : ''} />
                  {errors.phoneNumber && <span className="srfq-error">{errors.phoneNumber}</span>}
                </div>
              </div>

              <div className="srfq-field" style={{ maxWidth: '48%' }}>
                <label>Country <span className="required">*</span></label>
                <input type="text" name="country" value={form.country} onChange={handleChange} placeholder="e.g. United Arab Emirates" className={errors.country ? 'error' : ''} />
                {errors.country && <span className="srfq-error">{errors.country}</span>}
              </div>
            </div>

            <button type="submit" className="srfq-submit-btn" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request for Quotation'}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SubmitRFQPage;
